import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Mic, MicOff, Volume2, Trash2, Sparkles, Globe2 } from 'lucide-react';
import { apiPost, apiGet } from '@/lib/api';
import { toast } from 'sonner';

const STATES = { IDLE: 'idle', LISTENING: 'listening', PROCESSING: 'processing', SPEAKING: 'speaking' };

// Regional Indian languages — ElevenLabs multilingual_v2 supports all major Indic languages.
const REGIONAL_LANGUAGES = [
  { code: 'hi', label: 'हिन्दी · Hindi', bcp: 'hi-IN' },
  { code: 'ta', label: 'தமிழ் · Tamil', bcp: 'ta-IN' },
  { code: 'te', label: 'తెలుగు · Telugu', bcp: 'te-IN' },
  { code: 'bn', label: 'বাংলা · Bengali', bcp: 'bn-IN' },
  { code: 'mr', label: 'मराठी · Marathi', bcp: 'mr-IN' },
  { code: 'gu', label: 'ગુજરાતી · Gujarati', bcp: 'gu-IN' },
  { code: 'kn', label: 'ಕನ್ನಡ · Kannada', bcp: 'kn-IN' },
  { code: 'ml', label: 'മലയാളം · Malayalam', bcp: 'ml-IN' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ · Punjabi', bcp: 'pa-IN' },
  { code: 'or', label: 'ଓଡ଼ିଆ · Odia', bcp: 'or-IN' },
  { code: 'ur', label: 'اردو · Urdu', bcp: 'ur-IN' },
];

export default function VoiceAIOverlay({ isOpen, onClose, onTranscript, activeSubject, activeTopic, mode = 'browser' }) {
  const [state, setState] = useState(STATES.IDLE);
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [language, setLanguage] = useState('hi'); // Only used in premium mode
  const [quota, setQuota] = useState(null); // { minutes_used, minutes_limit, ... }
  const recognitionRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const processingRef = useRef(false);

  const isPremium = mode === 'premium';
  const activeLang = REGIONAL_LANGUAGES.find(l => l.code === language) || REGIONAL_LANGUAGES[0];
  const recognitionLang = isPremium ? activeLang.bcp : 'en-IN';

  // Fetch quota for premium mode
  useEffect(() => {
    if (isOpen && isPremium) {
      apiGet('/voice/quota').then(setQuota).catch(() => setQuota(null));
    }
  }, [isOpen, isPremium]);

  // Accent colors per mode — Browser=blue, Premium=purple/amber
  const accentPrimary = isPremium ? '#A855F7' : '#6366F1';
  const accentSecondary = isPremium ? '#F59E0B' : '#3B82F6';

  // Animated orb drawing
  const drawOrb = useCallback((ctx, width, height, level, currentState) => {
    ctx.clearRect(0, 0, width, height);
    const cx = width / 2;
    const cy = height / 2;
    const baseRadius = Math.min(width, height) * 0.22;
    const time = Date.now() / 1000;

    for (let ring = 3; ring >= 0; ring--) {
      const ringLevel = currentState === STATES.IDLE ? 0.15 : level;
      const ringRadius = baseRadius + 20 + ring * 18 + Math.sin(time * (1.5 + ring * 0.3)) * (8 + ringLevel * 25);
      const alpha = (0.04 + ringLevel * 0.06) * (1 - ring * 0.2);
      const gradient = ctx.createRadialGradient(cx, cy, ringRadius * 0.6, cx, cy, ringRadius);
      if (currentState === STATES.LISTENING) {
        gradient.addColorStop(0, isPremium ? `rgba(168, 85, 247, ${alpha})` : `rgba(99, 102, 241, ${alpha})`);
        gradient.addColorStop(1, isPremium ? `rgba(245, 158, 11, 0)` : `rgba(59, 130, 246, 0)`);
      } else if (currentState === STATES.SPEAKING) {
        gradient.addColorStop(0, `rgba(34, 197, 94, ${alpha})`);
        gradient.addColorStop(1, `rgba(16, 185, 129, 0)`);
      } else if (currentState === STATES.PROCESSING) {
        gradient.addColorStop(0, `rgba(251, 191, 36, ${alpha})`);
        gradient.addColorStop(1, `rgba(245, 158, 11, 0)`);
      } else {
        gradient.addColorStop(0, isPremium ? `rgba(168, 85, 247, ${alpha})` : `rgba(99, 102, 241, ${alpha})`);
        gradient.addColorStop(1, isPremium ? `rgba(245, 158, 11, 0)` : `rgba(59, 130, 246, 0)`);
      }
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    const points = 120;
    ctx.beginPath();
    for (let i = 0; i <= points; i++) {
      const angle = (i / points) * Math.PI * 2;
      const wobble = currentState === STATES.IDLE
        ? Math.sin(angle * 3 + time * 1.2) * 3 + Math.sin(angle * 5 + time * 0.8) * 2
        : Math.sin(angle * 4 + time * 2.5) * (6 + level * 20) + Math.sin(angle * 7 + time * 1.8) * (4 + level * 12) + Math.cos(angle * 2 + time * 3.2) * (3 + level * 8);
      const r = baseRadius + wobble;
      const x = cx + Math.cos(angle) * r;
      const y = cy + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();

    const orbGrad = ctx.createRadialGradient(cx - baseRadius * 0.3, cy - baseRadius * 0.3, baseRadius * 0.1, cx, cy, baseRadius * 1.2);
    if (currentState === STATES.LISTENING) {
      if (isPremium) { orbGrad.addColorStop(0, '#D8B4FE'); orbGrad.addColorStop(0.5, '#A855F7'); orbGrad.addColorStop(1, '#7C3AED'); }
      else { orbGrad.addColorStop(0, '#818CF8'); orbGrad.addColorStop(0.5, '#6366F1'); orbGrad.addColorStop(1, '#4F46E5'); }
    } else if (currentState === STATES.SPEAKING) {
      orbGrad.addColorStop(0, '#6EE7B7'); orbGrad.addColorStop(0.5, '#34D399'); orbGrad.addColorStop(1, '#10B981');
    } else if (currentState === STATES.PROCESSING) {
      orbGrad.addColorStop(0, '#FCD34D'); orbGrad.addColorStop(0.5, '#FBBF24'); orbGrad.addColorStop(1, '#F59E0B');
    } else {
      if (isPremium) { orbGrad.addColorStop(0, '#F0ABFC'); orbGrad.addColorStop(0.5, '#A855F7'); orbGrad.addColorStop(1, '#6D28D9'); }
      else { orbGrad.addColorStop(0, '#93C5FD'); orbGrad.addColorStop(0.5, '#6366F1'); orbGrad.addColorStop(1, '#4338CA'); }
    }
    ctx.fillStyle = orbGrad;
    ctx.fill();

    const shineGrad = ctx.createRadialGradient(cx - baseRadius * 0.25, cy - baseRadius * 0.35, 2, cx, cy, baseRadius * 0.8);
    shineGrad.addColorStop(0, 'rgba(255,255,255,0.35)');
    shineGrad.addColorStop(0.5, 'rgba(255,255,255,0.08)');
    shineGrad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = shineGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, baseRadius * 0.85, 0, Math.PI * 2);
    ctx.fill();

    const particleCount = currentState === STATES.IDLE ? 6 : 14;
    for (let i = 0; i < particleCount; i++) {
      const pAngle = (i / particleCount) * Math.PI * 2 + time * (0.3 + i * 0.05);
      const pDist = baseRadius + 35 + Math.sin(time * 1.5 + i * 1.2) * (15 + level * 20);
      const px = cx + Math.cos(pAngle) * pDist;
      const py = cy + Math.sin(pAngle) * pDist;
      const pSize = 1.5 + Math.sin(time * 2 + i) * 1 + level * 2;
      ctx.fillStyle = currentState === STATES.LISTENING ? (isPremium ? `rgba(216, 180, 254, ${0.4 + level * 0.4})` : `rgba(165, 180, 252, ${0.4 + level * 0.4})`) :
                      currentState === STATES.SPEAKING ? `rgba(110, 231, 183, ${0.4 + level * 0.4})` :
                      (isPremium ? `rgba(240, 171, 252, ${0.3 + level * 0.3})` : `rgba(147, 197, 253, ${0.3 + level * 0.3})`);
      ctx.beginPath();
      ctx.arc(px, py, pSize, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [isPremium]);

  // Animation loop
  useEffect(() => {
    if (!isOpen || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = 360 * dpr;
    canvas.height = 360 * dpr;
    ctx.scale(dpr, dpr);
    let currentLevel = 0;
    const animate = () => {
      currentLevel += (audioLevel - currentLevel) * 0.15;
      drawOrb(ctx, 360, 360, Math.min(1, currentLevel), state);
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, [isOpen, state, audioLevel, drawOrb]);

  const startAudioMonitoring = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const monitor = () => {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(avg / 128);
        requestAnimationFrame(monitor);
      };
      monitor();
    } catch { /* mic denied */ }
  };

  const stopAudioMonitoring = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null; }
    analyserRef.current = null;
    setAudioLevel(0);
  };

  // Pre-load browser voices
  useEffect(() => {
    window.speechSynthesis.getVoices();
    window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
  }, []);

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { toast.error('Speech recognition not supported. Try Chrome.'); return; }

    setTranscript('');
    setAiResponse('');
    processingRef.current = false;
    setState(STATES.LISTENING);
    startAudioMonitoring();

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = recognitionLang;
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      let text = '';
      for (let i = 0; i < event.results.length; i++) text += event.results[i][0].transcript;
      setTranscript(text);
    };
    recognition.onerror = (e) => { if (e.error !== 'aborted') { toast.error('Speech error: ' + e.error); } };
    recognition.onend = () => { /* natural end */ };
    recognition.start();
  };

  const stopListening = () => {
    if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
    stopAudioMonitoring();
    if (processingRef.current) return;
    processingRef.current = true;
    const transcriptEl = document.querySelector('[data-testid="voice-transcript-text"]');
    const currentText = transcriptEl?.textContent?.trim() || '';
    if (currentText) {
      processTranscript(currentText);
    } else {
      setState(STATES.IDLE);
      processingRef.current = false;
    }
  };

  const processTranscript = async (text) => {
    setState(STATES.PROCESSING);
    if (onTranscript) {
      const response = await onTranscript(text);
      if (response) {
        setAiResponse(response);
        setState(STATES.SPEAKING);
        if (isPremium) {
          speakWithElevenLabs(response);
        } else {
          speakWithBrowser(response, 'en-US');
        }
      } else {
        setState(STATES.IDLE);
        processingRef.current = false;
      }
    }
  };

  const speakWithElevenLabs = async (text) => {
    // Clean + limit for credit efficiency
    let cleanText = text.replace(/\*\*/g, '').replace(/\*/g, '').replace(/`/g, '').replace(/###/g, '').replace(/##/g, '').replace(/#/g, '');
    const words = cleanText.split(/\s+/);
    if (words.length > 150) cleanText = words.slice(0, 150).join(' ') + '...';

    try {
      const ttsRes = await apiPost('/voice/tts', { text: cleanText, language });
      if (ttsRes.usage) setQuota(ttsRes.usage);
      const audio = new Audio(`data:audio/mpeg;base64,${ttsRes.audio_base64}`);
      audioRef.current = audio;

      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioCtx.createMediaElementSource(audio);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        source.connect(audioCtx.destination);
        analyserRef.current = analyser;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const monitorPlayback = () => {
          if (!analyserRef.current) return;
          analyser.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          setAudioLevel(avg / 128);
          if (audioRef.current && !audioRef.current.paused) requestAnimationFrame(monitorPlayback);
        };

        audio.onplay = () => monitorPlayback();
        audio.onended = () => { setState(STATES.IDLE); setAudioLevel(0); analyserRef.current = null; audioCtx.close(); processingRef.current = false; };
        audio.onerror = () => { setState(STATES.IDLE); setAudioLevel(0); processingRef.current = false; };
        await audio.play();
      } catch {
        audio.onended = () => { setState(STATES.IDLE); setAudioLevel(0); processingRef.current = false; };
        audio.onerror = () => { setState(STATES.IDLE); setAudioLevel(0); processingRef.current = false; };
        await audio.play();
        const fakeInterval = setInterval(() => { setAudioLevel(0.3 + Math.random() * 0.4); }, 100);
        audio.onended = () => { clearInterval(fakeInterval); setState(STATES.IDLE); setAudioLevel(0); processingRef.current = false; };
      }
    } catch (e) {
      // Handle quota exhaustion specifically (429 from backend)
      const errMsg = (e && e.message) ? String(e.message) : '';
      if (errMsg.includes('quota exhausted') || errMsg.includes('429')) {
        toast.error('Monthly premium voice quota exhausted. We are working on increasing the quota to 30 mins for you in 1-2 month max. Please bear with us until then.', { duration: 8000 });
      } else {
        toast.error('Premium voice unavailable, using browser voice');
      }
      // Auto-fallback to browser voice in selected language BCP
      speakWithBrowser(text, activeLang.bcp);
    }
  };

  const speakWithBrowser = (text, langCode = 'en-US') => {
    window.speechSynthesis.cancel();
    const cleanText = text.replace(/\*\*/g, '').replace(/\*/g, '').replace(/`/g, '').replace(/###/g, '').replace(/##/g, '').replace(/#/g, '');
    const truncated = cleanText.length > 400 ? cleanText.slice(0, 400) + '...' : cleanText;
    const utterance = new SpeechSynthesisUtterance(truncated);
    const voices = window.speechSynthesis.getVoices();

    // Prefer a female voice in the requested language; fallback to English female
    const langFemales = voices.filter(v => v.lang.startsWith(langCode.split('-')[0]) && /female|zira|samantha|karen|tessa|moira|veena|rishi/i.test(v.name));
    const langAny = voices.filter(v => v.lang.startsWith(langCode.split('-')[0]));
    const englishFemales = ['Google UK English Female', 'Google US English', 'Samantha', 'Karen', 'Microsoft Zira'];

    let chosen = langFemales[0] || langAny[0];
    if (!chosen) {
      for (const name of englishFemales) {
        const found = voices.find(v => v.name.includes(name));
        if (found) { chosen = found; break; }
      }
    }
    if (chosen) utterance.voice = chosen;
    utterance.lang = langCode;
    utterance.rate = 0.92;
    utterance.pitch = 1.1;
    const animateInterval = setInterval(() => { if (window.speechSynthesis.speaking) setAudioLevel(0.3 + Math.random() * 0.4); }, 100);
    utterance.onend = () => { clearInterval(animateInterval); setState(STATES.IDLE); setAudioLevel(0); processingRef.current = false; };
    utterance.onerror = () => { clearInterval(animateInterval); setState(STATES.IDLE); setAudioLevel(0); processingRef.current = false; };
    window.speechSynthesis.speak(utterance);
  };

  const clearTranscript = () => {
    setTranscript('');
    setAiResponse('');
    if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
    stopAudioMonitoring();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    window.speechSynthesis.cancel();
    analyserRef.current = null;
    setState(STATES.IDLE);
    setAudioLevel(0);
    processingRef.current = false;
  };

  const handleClose = () => {
    clearTranscript();
    onClose();
  };

  if (!isOpen) return null;

  const stateLabel = state === STATES.LISTENING ? 'Listening...' : state === STATES.PROCESSING ? 'Thinking...' : state === STATES.SPEAKING ? 'Speaking...' : 'Tap to speak';
  const stateColor = state === STATES.LISTENING ? (isPremium ? 'text-fuchsia-400' : 'text-indigo-400') : state === STATES.PROCESSING ? 'text-amber-400' : state === STATES.SPEAKING ? 'text-emerald-400' : 'text-slate-400';

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 flex flex-col items-center justify-center animate-fade-in overflow-y-auto" data-testid="voice-ai-overlay">
      {/* Close */}
      <button onClick={handleClose} data-testid="voice-close-btn"
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-all">
        <X className="w-5 h-5" />
      </button>

      {/* Top bar: mode + subject + quota */}
      <div className="absolute top-6 left-6 text-left" data-testid="voice-mode-badge">
        <div className="flex items-center gap-2">
          {isPremium ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-purple-500/20 to-amber-400/20 border border-purple-400/30 text-[10px] font-heading text-purple-200 uppercase tracking-wider">
              <Sparkles className="w-3 h-3" /> Voice Tutor · Regional Language
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-500/20 border border-sky-400/30 text-[10px] font-heading text-sky-200 uppercase tracking-wider">
              <Mic className="w-3 h-3" /> Voice Tutor · English
            </span>
          )}
          <p className="text-xs text-white/30 font-body">Gurukul Veda · Voice Tutor</p>
        </div>
        {activeSubject && <p className="text-xs text-white/50 font-body mt-1">{activeSubject} {activeTopic ? `· ${activeTopic}` : ''}</p>}
        {isPremium && quota && (
          <div className="mt-2 flex items-center gap-2" data-testid="voice-quota-meter">
            <div className="w-32 h-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-purple-400 to-amber-300 transition-all"
                style={{ width: `${Math.min(100, (quota.minutes_used / quota.minutes_limit) * 100)}%` }} />
            </div>
            <span className="text-[10px] text-white/40 font-body">{quota.minutes_used} / {quota.minutes_limit} min</span>
          </div>
        )}
      </div>

      {/* Language picker (Premium only) */}
      {isPremium && (
        <div className="mb-6 flex items-center gap-2 bg-white/5 backdrop-blur rounded-full px-4 py-2 border border-white/10">
          <Globe2 className="w-4 h-4 text-purple-300" />
          <select data-testid="voice-language-select" value={language} onChange={e => setLanguage(e.target.value)}
            className="bg-transparent text-xs text-white/80 font-body outline-none cursor-pointer">
            {REGIONAL_LANGUAGES.map(l => (
              <option key={l.code} value={l.code} className="bg-slate-900 text-white">{l.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Animated Orb */}
      <div className="relative mb-8">
        <canvas ref={canvasRef} style={{ width: 360, height: 360 }} className="cursor-pointer"
          onClick={state === STATES.IDLE ? startListening : state === STATES.LISTENING ? stopListening : undefined}
          data-testid="voice-orb" />
      </div>

      {/* State label */}
      <p className={`font-heading text-sm font-medium ${stateColor} mb-4 transition-colors`} data-testid="voice-state">
        {stateLabel}
      </p>

      {/* Transcript */}
      {transcript && (
        <div className="max-w-md w-full mx-auto px-6 mb-4 animate-slide-up">
          <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className={`text-[10px] ${isPremium ? 'text-purple-400' : 'text-indigo-400'} font-heading mb-1 uppercase tracking-wider`}>You said</p>
                <p className="text-sm text-white/90 font-body leading-relaxed" data-testid="voice-transcript-text">{transcript}</p>
              </div>
              <button onClick={clearTranscript} data-testid="voice-clear-btn"
                className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Response */}
      {aiResponse && (
        <div className="max-w-md w-full mx-auto px-6 mb-4 animate-slide-up">
          <div className="bg-emerald-500/5 backdrop-blur rounded-2xl p-4 border border-emerald-500/20">
            <p className="text-[10px] text-emerald-400 font-heading mb-1 uppercase tracking-wider">Gurukul Veda</p>
            <p className="text-sm text-white/80 font-body leading-relaxed line-clamp-6">{aiResponse}</p>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-4 mt-2">
        {state === STATES.IDLE && (
          <button onClick={startListening} data-testid="voice-start-btn"
            className={`px-8 py-3 rounded-full ${isPremium ? 'bg-gradient-to-r from-purple-500 via-fuchsia-500 to-amber-400' : 'bg-gradient-to-r from-sky-500 to-blue-500'} text-white font-heading text-sm hover:shadow-lg transition-all flex items-center gap-2`}>
            <Mic className="w-4 h-4" /> Start Speaking {isPremium ? `· ${activeLang.label.split(' · ')[1]}` : ''}
          </button>
        )}
        {state === STATES.LISTENING && (
          <button onClick={stopListening} data-testid="voice-stop-btn"
            className="px-8 py-3 rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white font-heading text-sm hover:shadow-lg transition-all flex items-center gap-2 animate-pulse">
            <MicOff className="w-4 h-4" /> Stop & Send
          </button>
        )}
        {state === STATES.SPEAKING && (
          <button onClick={() => { if (audioRef.current) audioRef.current.pause(); window.speechSynthesis.cancel(); setState(STATES.IDLE); setAudioLevel(0); processingRef.current = false; }}
            className="px-6 py-3 rounded-full bg-white/10 text-white/70 font-heading text-sm hover:bg-white/20 transition-all flex items-center gap-2">
            <Volume2 className="w-4 h-4" /> Stop Playback
          </button>
        )}
      </div>

      {/* Hint */}
      <p className="text-[10px] text-white/20 font-body mt-6 max-w-sm text-center px-6">
        {state === STATES.IDLE
          ? (isPremium
              ? `Tap to speak in ${activeLang.label.split(' · ')[1]} · Regional Language voice · 10 min/month`
              : 'Tap to speak in English · Unlimited')
          : state === STATES.LISTENING ? 'Speak naturally — tap again when done' : ''}
      </p>
    </div>
  );
}
